<template>
    <header class="app-header">
      <div class="logo-and-title">
        <router-link to="/" class="brand-link" aria-label="Attractive Salon and Academy — Home">
          <img :src="logo" alt="Attractive Unisex Salon and Academy logo" class="logo" />
          <p class="salon-title">Attractive Salon and Academy</p>
        </router-link>
      </div>
      <button class="mobile-nav-toggle" @click="toggleMobileNav" aria-label="Toggle navigation" :aria-expanded="isMobileNavOpen">
        <span class="hamburger-icon"></span>
      </button>
      <nav class="main-nav" :class="{ 'mobile-nav-open': isMobileNavOpen }">
        <router-link to="/" @click="closeMobileNav">Home</router-link>
        <router-link to="/about" @click="closeMobileNav">About</router-link>
        <router-link to="/services" @click="closeMobileNav">Service</router-link>
        <router-link
          v-if="isUserLoggedIn && canAccessAdmin"
          to="/admin"
          @click="closeMobileNav"
        >
          Admin
        </router-link>
        <a
          v-if="isUserLoggedIn"
          href="#"
          @click.prevent="handleLogoutAndCloseNav"
          class="nav-link logout-link"
        >
          Logout
        </a>
        <router-link v-else to="/login" @click="closeMobileNav">Login</router-link>
      </nav>
    </header>
</template>
  
<script lang="ts">
  import { defineComponent } from 'vue';
  import logo from '@/assets/logo.png';
  import { useAuthStore } from '@/store/auth.store';
  import { hasAdminPortalAccess } from '@/constants/roles';
  import { mapState, mapActions } from 'pinia';
  
  interface User {
    id: string;
    email: string;
    firstName?: string;
    first_name?: string;
    lastName?: string;
    last_name?: string;
    role?: { name: string };
  }
  
  export default defineComponent({
    name: 'AppHeader',
    data() {
      return {
        logo: logo as string,
        isMobileNavOpen: false,
      };
    },
    computed: {
      ...mapState(useAuthStore, {
        isUserLoggedIn: (store) => store.isUserLoggedIn,
        getUser: (store) => store.getUser as User | null, 
      }),
      canAccessAdmin(): boolean {
        return hasAdminPortalAccess(this.getUser?.role?.name);
      },
    },
    methods: {
      ...mapActions(useAuthStore, ['logout']),
      
      toggleMobileNav(): void {
        this.isMobileNavOpen = !this.isMobileNavOpen;
      },
      closeMobileNav(): void {
        this.isMobileNavOpen = false;
      },
      handleLogoutAndCloseNav(): void {
        this.logout();
        this.closeMobileNav();
      }
    },
  });
</script>
  
<style scoped>
  .app-header {
    background-color: var(--brand-black);
    color: var(--text-light);
    padding: 15px 30px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 1001;
    min-height: var(--header-height);
    box-sizing: border-box;
  }
  
  .logo-and-title {
    display: flex;
    align-items: center;
  }

  .brand-link {
    display: flex;
    align-items: center;
    text-decoration: none;
    color: inherit;
  }
  
  .logo {
    height: 50px;
    width: auto;
    margin-right: 15px;
  }
  
  .salon-title {
    margin: 0;
    font-size: 1.5em;
    color: var(--brand-gold);
    font-weight: bold;
  }
  
  .mobile-nav-toggle {
    display: none;
    background: none;
    border: none;
    cursor: pointer;
    padding: 10px;
    z-index: 1001;
  }
  
  .hamburger-icon {
    display: block;
    width: 25px;
    height: 3px;
    background-color: var(--text-light);
    position: relative;
    transition: background-color 0.3s ease;
  }
  
  .hamburger-icon::before,
  .hamburger-icon::after {
    content: '';
    position: absolute;
    width: 25px;
    height: 3px;
    background-color: var(--text-light);
    left: 0;
    transition: transform 0.3s ease, top 0.3s ease, bottom 0.3s ease;
  }
  
  .hamburger-icon::before {
    top: -8px;
  }
  
  .hamburger-icon::after {
    bottom: -8px;
  }
  
  .mobile-nav-toggle[aria-expanded="true"] .hamburger-icon {
    background-color: transparent;
  }
  
  .mobile-nav-toggle[aria-expanded="true"] .hamburger-icon::before {
    transform: rotate(45deg);
    top: 0;
  }
  
  .mobile-nav-toggle[aria-expanded="true"] .hamburger-icon::after {
    transform: rotate(-45deg);
    bottom: 0;
  }
  
  .main-nav {
    display: flex;
    align-items: center;
  }
  
  .main-nav a {
    font-weight: 500;
    color: var(--text-light);
    text-decoration: none;
    margin-left: 15px;
    padding: 5px 0;
    transition: color 0.3s ease;
  }
  
  .main-nav a:first-child {
    margin-left: 0;
  }
  
  .nav-separator {
    color: var(--brand-gold);
    margin-left: 15px;
    margin-right: 0;
  }

  .main-nav a:hover,
  .main-nav a.router-link-exact-active {
    color: var(--brand-gold);
    border-bottom: 2px solid var(--brand-gold);
  }

  .logout-link, .nav-link {
    font-weight: 500;
    color: var(--text-light);
    text-decoration: none;
    margin-left: 15px;
    padding: 5px 0;
    transition: color 0.3s ease;
    cursor: pointer;
  }
  
  .logout-link:hover, .nav-link:hover {
    color: var(--brand-gold);
  }
  
  @media (max-width: 768px) {
    .app-header {
      padding: 12px 16px;
    }
    .logo {
      height: 40px;
      margin-right: 10px;
    }
    .salon-title {
      font-size: 1.1rem;
      line-height: 1.2;
      max-width: calc(100vw - 120px);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .mobile-nav-toggle {
      display: block;
    }
    .main-nav {
      display: none;
      flex-direction: column;
      align-items: flex-start;
      position: absolute;
      top: 100%;
      left: 0;
      width: 100%;
      background-color: var(--brand-black);
      padding: 20px;
      box-shadow: 0 4px 6px rgba(0,0,0,0.1);
      z-index: 1000;
    }
    .main-nav.mobile-nav-open {
      display: flex;
    }
    .main-nav a,
    .main-nav .nav-separator,
    .main-nav .logout-link {
      margin-left: 0;
      margin-bottom: 15px;
      width: 100%;
      padding: 10px 0;
      border-bottom: 1px solid rgba(218, 165, 32, 0.2);
    }
    .main-nav a:last-child,
    .main-nav .logout-link:last-child {
      margin-bottom: 0;
      border-bottom: none;
    }
    .main-nav .nav-separator {
      display: none;
    }
    .main-nav a:hover,
    .main-nav a.router-link-exact-active {
      border-bottom: 1px solid var(--brand-gold);
    }
  }

  @media (max-width: 576px) {
    .salon-title {
      font-size: 0.95rem;
      max-width: calc(100vw - 100px);
    }
  }
</style>